import { Flex, slotHeaderState, SplitView } from '@approvalmax/ui/src/components';
import { useAtomValue, useSetAtom } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import { constants } from 'modules/common';
import { domain } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { HeaderActions } from 'pages/requestForm/components';
import { usePageRedesign } from 'pages/requestForm/hooks/usePageRedesign';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { memo, PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useMount, useUnmount } from 'react-use';
import {
    ComponentErrorBoundary,
    FileMenuDropdown,
    SplitViewGoToBar,
    splitViewGoToBarSettingsState,
} from 'shared/components';
import { useSplitViewContent } from 'shared/hooks/useSplitViewContent';
import { useStopCheckOcrStatus } from 'shared/hooks/useStopCheckOcrStatus';
import { ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { AttachmentToolbar, LeftItemContent, ocrAttachmentIdState, PreviewToggleButton } from './components';
import { useDefaultSplitViewOpen, useObserveSplitViewItemWidth } from './SplitViewWrapper.hooks';
import { defaultSplitViewWidthState } from './SplitViewWrapper.states';

const { commonConstants } = constants;

export const SplitViewWrapper = memo<PropsWithChildren>((props) => {
    const { children } = props;

    const request = useSelector(requestSelector);

    const [splitViewFirstItemTitle, setSplitViewFirstItemTitle] = useState('');

    const setSlotHeader = useSetAtom(slotHeaderState);
    const resetSlotHeader = useResetAtom(slotHeaderState);
    const resetOcrAttachmentId = useResetAtom(ocrAttachmentIdState);
    const splitViewGoToBarSettings = useAtomValue(splitViewGoToBarSettingsState);
    const setContentSplitView = useSetAtom(contentSplitViewState);
    const defaultSplitViewWidth = useAtomValue(defaultSplitViewWidthState);

    const { splitViewItemRef, splitViewItemWidth } = useObserveSplitViewItemWidth();
    const stopCheckOcrStatus = useStopCheckOcrStatus();
    const { activeAttachment, attachments, goToNextDocument, goToPrevDocument, attachmentIndex } =
        useSplitViewContent();
    const { isPageRedesign } = usePageRedesign({ exclude: [domain.IntegrationCode.XeroContact] });

    const isBatchPayment =
        request.integrationCode &&
        [
            domain.IntegrationCode.XeroBillBatchPayment,
            domain.IntegrationCode.XeroAirwallexBatchPayment,
            domain.IntegrationCode.XeroAmaxPayBatchPayment,
        ].includes(request?.integrationCode);

    useMount(() => {
        if (
            request.integrationCode &&
            !constants.xeroConstants.batchPaymentIntegrationCodes.includes(request.integrationCode) &&
            request.attachments.length > 0
        ) {
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToRequest,
                attachmentId: request.attachments[0].id,
            });
        }
    });

    useUnmount(() => {
        stopCheckOcrStatus();
        resetSlotHeader();
        resetOcrAttachmentId();
    });

    useEffect(() => {
        if (!isBatchPayment && !isPageRedesign) {
            setSlotHeader(<PreviewToggleButton />);
        }
    }, [isBatchPayment, isPageRedesign, request.integrationCode, setSlotHeader]);

    useDefaultSplitViewOpen(request);

    const onCloseSplitView = useCallback(() => {
        setSplitViewFirstItemTitle('');
        setContentSplitView({ mode: ContentSplitViewMode.Empty });
    }, [setContentSplitView]);

    return (
        <SplitView>
            <SplitView.Item
                ref={splitViewItemRef}
                title={<FileMenuDropdown attachments={attachments} title={splitViewFirstItemTitle} request={request} />}
                showInFullScreen
                hideToggleFullscreen={!isBatchPayment}
                onClose={onCloseSplitView}
                preferredSize={defaultSplitViewWidth ?? commonConstants.requestMinLeftPanelWidth}
                snap={false}
                minSize={50}
                customToolbar={
                    splitViewGoToBarSettings.show ? (
                        <SplitViewGoToBar
                            downloadButtonProps={{ url: activeAttachment?.url || '' }}
                            paginationProps={{
                                currentIndex: (attachmentIndex || 0) + 1,
                                total: attachments.length,
                                goToNextDocument,
                                goToPrevDocument,
                            }}
                        />
                    ) : undefined
                }
                actions={
                    activeAttachment ? (
                        <AttachmentToolbar
                            activeAttachment={activeAttachment}
                            attachmentIndex={attachmentIndex}
                            attachments={attachments}
                            request={request}
                            goToNextDocument={goToNextDocument}
                            goToPrevDocument={goToPrevDocument}
                            collapsed={splitViewItemWidth < commonConstants.requestMinLeftPanelWidth}
                        />
                    ) : null
                }
            >
                <ComponentErrorBoundary>
                    <LeftItemContent
                        setSplitViewFirstItemTitle={setSplitViewFirstItemTitle}
                        splitViewFirstItemTitle={splitViewFirstItemTitle}
                        goToPrevDocument={goToPrevDocument}
                        goToNextDocument={goToNextDocument}
                        activeAttachment={activeAttachment}
                        activeRequest={request}
                    />
                </ComponentErrorBoundary>
            </SplitView.Item>

            <SplitView.Item
                title={request.displayName}
                renderByDefault
                hideToggleFullscreen={!isBatchPayment}
                snap={false}
                minSize={50}
                actions={
                    !isBatchPayment && (
                        <Flex alignItems='center'>{isPageRedesign ? <HeaderActions /> : <PreviewToggleButton />}</Flex>
                    )
                }
            >
                {children}
            </SplitView.Item>
        </SplitView>
    );
});

SplitViewWrapper.displayName = 'SplitViewWrapper';
